// Persistent top nav. Brand left, nav links center-right, auth controls far
// right. The auth area follows best-practice: slim "Log in" / "Register"
// buttons when signed out; the account email + "Log out" when signed in.
//
// At tablet / mobile widths (see MENU_BREAKPOINT + siteTheme.css) the nav and the
// auth controls fold into a hamburger menu: a labelled toggle button
// (aria-expanded / aria-controls) that opens a full-width panel under the header.
// Closes on: Escape (focus returns to the button), choosing a link, tapping
// outside, or the viewport growing back to desktop width.

import { Events, on, emit } from './eventBus.js'
import { getActivePage, setActivePage } from './siteRouter.js'
import { getAccountInfo, logout } from '../game/accountManager.js'
import { GAME_VERSION, BUILD_DATE } from '../config/version.js'

// Keep in sync with the @media (max-width) breakpoint in siteTheme.css.
const MENU_BREAKPOINT = 1180 // (was 1100; the version number beside the logo needs a little more room)

const NAV = [
  { id: 'home', label: 'HOME' },
  { id: 'play', label: 'PLAY' },
  { id: 'registry', label: 'REGISTRY' },
  { id: 'shop', label: 'SHOP' },
  { id: 'guide', label: 'GAME GUIDE' },
  { id: 'overview', label: 'OVERVIEW' },
  // VIRTUAL LAND is off the menu for now. The page itself is NOT deleted (index.html, main.js and siteRouter.js still have it, and
  // #vland still opens it) — to bring it back, add  { id: 'vland', label: 'VIRTUAL LAND' },  here where you want it.
]

export function mountSiteHeader({ root }) {
  root.innerHTML = `
    <div class="site-brand">
      <span class="site-brand-en">MAO KINGDOM</span>
      <span class="site-brand-cn">猫王国</span>
      <span class="site-brand-ver" title="Mao Kingdom ${GAME_VERSION} · build ${BUILD_DATE}">${GAME_VERSION}</span>
    </div>
    <button class="site-burger" type="button" aria-label="Open menu" aria-expanded="false"
            aria-controls="site-menu" data-site-burger>
      <span class="site-burger__bar"></span>
      <span class="site-burger__bar"></span>
      <span class="site-burger__bar"></span>
    </button>
    <div class="site-menu" id="site-menu" data-site-menu>
      <nav class="site-nav" aria-label="Main">
        ${NAV.map((n) => `<a href="#${n.id}" data-page-link="${n.id}">${n.label}</a>`).join('')}
      </nav>
      <div class="site-auth" data-site-auth></div>
    </div>
  `

  const burger = root.querySelector('[data-site-burger]')
  const menu = root.querySelector('[data-site-menu]')

  const isMenuMode = () => window.innerWidth <= MENU_BREAKPOINT
  const setMenuOpen = (open, { returnFocus = false } = {}) => {
    root.classList.toggle('menu-open', open)
    burger.setAttribute('aria-expanded', String(open))
    burger.setAttribute('aria-label', open ? 'Close menu' : 'Open menu')
    if (!open && returnFocus) burger.focus()
  }
  const menuOpen = () => root.classList.contains('menu-open')

  burger.addEventListener('click', () => setMenuOpen(!menuOpen()))
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && menuOpen()) setMenuOpen(false, { returnFocus: true })
  })
  // Tap/click anywhere outside the header closes it.
  document.addEventListener('pointerdown', (e) => {
    if (menuOpen() && !root.contains(e.target)) setMenuOpen(false)
  })
  // Growing back to desktop width: drop the open state so it can't get stuck.
  window.addEventListener('resize', () => {
    if (menuOpen() && !isMenuMode()) setMenuOpen(false)
  })

  root.querySelectorAll('[data-page-link]').forEach((a) => {
    a.addEventListener('click', (e) => {
      e.preventDefault()
      setActivePage(a.dataset.pageLink)
      setMenuOpen(false)
    })
  })

  const authEl = root.querySelector('[data-site-auth]')

  const renderAuth = () => {
    const { loggedIn, email, isAdmin } = getAccountInfo()
    if (loggedIn) {
      const label = email || 'Account'
      authEl.innerHTML = `
        <span class="site-auth__who" title="${label}">${isAdmin ? '★ ' : ''}${label}</span>
        <button class="site-auth__btn" data-auth-logout>Log out</button>
      `
      authEl.querySelector('[data-auth-logout]').addEventListener('click', async () => {
        setMenuOpen(false)
        await logout()
        setActivePage('home')
      })
    } else {
      authEl.innerHTML = `
        <button class="site-auth__btn" data-auth-login>Log in</button>
        <button class="site-auth__btn site-auth__btn--primary" data-auth-register>Register</button>
      `
      authEl.querySelector('[data-auth-login]').addEventListener('click', () => {
        setMenuOpen(false)
        emit(Events.OpenLogin)
      })
      authEl.querySelector('[data-auth-register]').addEventListener('click', () => {
        setMenuOpen(false)
        setActivePage('play')      // adoption flow lives on the play view
        emit(Events.StartAdoption) // and (re)open it
      })
    }
  }

  const refresh = () => {
    const active = getActivePage()
    root.querySelectorAll('[data-page-link]').forEach((a) => {
      const on = a.dataset.pageLink === active
      a.classList.toggle('active', on)
      if (on) a.setAttribute('aria-current', 'page')
      else a.removeAttribute('aria-current')
    })
  }

  // Publish the header's real height so #site-main sits directly beneath it
  // (see siteTheme.css).
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(() => {
      document.documentElement.style.setProperty('--site-header-live', `${root.offsetHeight}px`)
    }).observe(root)
  }

  on(Events.PageChange, refresh)
  on(Events.AccountReady, renderAuth)
  refresh()
  renderAuth()
}
